import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resumeSessionByCard } from "../services/apiService";
import { useErrorHandler } from "../utils/errorHandler";
import analyticsService from "../services/analyticsService";

const MAX_ATTEMPTS = 3;

export default function ResumeSessionComponent() {
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();
  const location = useLocation();
  const { evcpData, connector, reference } = location.state || {};

  const [digits, setDigits] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);
  const [locked, setLocked] = useState(false);

  const hasRequiredData = Boolean(evcpData?.reference && connector?.id);

  // Vérification des informations nécessaires
  useEffect(() => {
    if (!hasRequiredData) {
      navigate("/error", {
        state: { errorText: t("resumeSession.errors.missingInfo") },
      });
      return;
    }
    analyticsService.pushPageView("resume_session_verification", {
      station_id: evcpData.reference,
    });
  }, [hasRequiredData, evcpData, navigate, t]);

  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setDigits(value);
    if (error) setError("");
  };

  const handleSubmit = async () => {
    if (locked || isSubmitting) return;
    if (digits.length !== 4) {
      setError(t("resumeSession.errors.invalidFormat"));
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const data = await resumeSessionByCard(connector.id, digits);

      sessionStorage.setItem("accessToken", data.accessToken);
      sessionStorage.setItem("refreshToken", data.refreshToken);

      analyticsService.pushEvent("resume_session_success", {
        station_id: evcpData.reference,
      });

      navigate("/start-charging", {
        state: {
          accessToken: data.accessToken,
          connector,
          evcpData,
          finishedTransaction: data.finishedTransaction || null,
        },
      });
    } catch (err) {
      const httpStatus = err?.status;

      if (httpStatus === 401) {
        // Code CB incorrect : décrémente le compteur de tentatives
        const remaining = attemptsLeft - 1;
        setAttemptsLeft(remaining);
        setDigits("");
        analyticsService.pushEvent("resume_session_failed", {
          station_id: evcpData.reference,
          reason: "card_mismatch",
          attempts_left: remaining,
        });

        if (remaining <= 0) {
          setLocked(true);
          setError(t("resumeSession.errors.tooManyAttempts"));
        } else {
          setError(t("resumeSession.errors.wrongCard", { count: remaining }));
        }
      } else if (httpStatus === 404) {
        // La session s'est terminée entre le scan et la validation → parcours standard
        analyticsService.pushEvent("resume_session_failed", {
          station_id: evcpData.reference,
          reason: "no_session",
        });
        navigate(`/${reference}`, { replace: true });
      } else {
        setError(handleError(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  if (!hasRequiredData) {
    return null;
  }

  const isSubmitDisabled = digits.length !== 4 || locked || isSubmitting;

  return (
    <div className="bg-white flex flex-col items-center justify-center p-4 pt-36">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center text-center space-y-3">
          <img src="/Shield_Check.svg" alt="" className="w-16 h-16" />
          <h2 className="auth-title">{t("resumeSession.title")}</h2>
          <p className="displayed-error-message">
            {t("resumeSession.sessionActive")}
          </p>
        </div>

        <div className="text-input-group mt-4 w-full" onKeyDown={handleKeyDown}>
          <label className="text-label">{t("resumeSession.cardLabel")}</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            pattern="[0-9]*"
            maxLength={4}
            className={`styled-input text-center tracking-[0.5em] ${
              error ? "border-red-500" : ""
            }`}
            value={digits}
            onChange={handleChange}
            disabled={locked || isSubmitting}
            placeholder="••••"
            aria-label={t("resumeSession.cardLabel")}
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>

        <button
          className={`custom-button ${isSubmitDisabled ? "disabled-button" : ""}`}
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          <span className="custom-button-text">
            {isSubmitting
              ? t("resumeSession.verifying")
              : t("resumeSession.validate")}
          </span>
        </button>
      </div>
    </div>
  );
}
